import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, RotateCcw, ArrowLeft, Archive } from "lucide-react";

const API = "/api";

export default function DeletedStudents() {
  const { getAuthHeader } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState({ open: false, student: null });
  const [deleting, setDeleting] = useState(false);

  const fetchDeleted = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/students/deleted`, { headers: getAuthHeader() });
      const data = response.data;
      setStudents(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      toast.error("Failed to load deleted students");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const handleRestore = async (studentId, name) => {
    setRestoring(studentId);
    try {
      await axios.post(`${API}/students/${studentId}/restore`, {}, { headers: getAuthHeader() });
      toast.success(`${name} has been restored!`);
      fetchDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore student");
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async () => {
    const student = permanentDeleteDialog.student;
    if (!student) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/students/${student.id}/permanent`, { headers: getAuthHeader() });
      toast.success(`${student.name} permanently deleted.`);
      setPermanentDeleteDialog({ open: false, student: null });
      fetchDeleted();
    } catch (error) {
      toast.error("Failed to permanently delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="deleted-students-page">
      <Link to="/students" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Students
      </Link>

      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-lg bg-amber-100">
          <Archive className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Deleted Students</h1>
          <p className="text-sm text-slate-500">{students.length} archived student{students.length !== 1 ? "s" : ""} — restore or permanently delete</p>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Archived Records</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No deleted students. All data is safe!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Student Name</TableHead>
                  <TableHead className="font-semibold text-center">Credits (Remaining)</TableHead>
                  <TableHead className="font-semibold text-center">Total Purchased</TableHead>
                  <TableHead className="font-semibold text-center">Deleted On</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id} data-testid={`deleted-student-${student.id}`}>
                    <TableCell className="font-medium text-slate-900">{student.name}</TableCell>
                    <TableCell className="text-center">{student.remaining_classes}</TableCell>
                    <TableCell className="text-center">{student.total_purchased_classes}</TableCell>
                    <TableCell className="text-center text-slate-500 text-sm">
                      {student.deleted_at ? new Date(student.deleted_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        onClick={() => handleRestore(student.id, student.name)}
                        disabled={restoring === student.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`restore-btn-${student.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        {restoring === student.id ? "Restoring..." : "Restore"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPermanentDeleteDialog({ open: true, student })}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`permanent-delete-btn-${student.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Permanent
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Permanent Delete Confirmation */}
      <Dialog open={permanentDeleteDialog.open} onOpenChange={(open) => !open && setPermanentDeleteDialog({ open: false, student: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Permanently Delete Student</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-slate-600">
              Are you sure you want to <strong>permanently delete</strong> <strong>{permanentDeleteDialog.student?.name}</strong>?
            </p>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm font-semibold">This cannot be undone!</p>
              <p className="text-red-600 text-xs mt-1">All class records, account data, and student information will be permanently removed from the system.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermanentDeleteDialog({ open: false, student: null })}>Cancel</Button>
            <Button onClick={handlePermanentDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting..." : "Delete Forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
